import React, { useState, useRef } from 'react';
import { Box, Typography, TextField, Button, Skeleton } from '@mui/material';
import { useParams } from 'react-router-dom';
import { sanitizeNumeric } from 'utils/number';
import { userService } from 'components/api/users';
import { generateIdempotencyKey } from 'utils/idempotency';
import { Alert } from 'components/alert/alert';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { showToast } from 'store/toastSlice';
import { fetchUserById } from 'store/usersSlice';
import ConfirmModal from 'components/modals/ConfirmModal';
import styles from './ChangeBalance.module.scss';

interface ChangeBalanceProps {
  title: string;
  type: 'increase' | 'decrease';
}

const inputStyles = {
  '& .MuiInputBase-input': {
    color: 'var(--text-primary)',
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'var(--text-primary) !important',
    opacity: '1 !important',
  },
  '& .MuiInputBase-input:disabled': {
    WebkitTextFillColor: 'var(--text-primary) !important',
    color: 'var(--text-primary) !important',
    opacity: '0.5 !important',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: 'var(--text-muted)', opacity: 0.5 },
    '&:hover fieldset': { borderColor: 'var(--text-secondary)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--accent-blue)' },
    '&.Mui-disabled fieldset': { borderColor: 'var(--text-muted)', opacity: 0.3 },
  },
  '& .MuiInputLabel-root': {
    color: 'var(--text-secondary)',
    '&.Mui-disabled': { color: 'var(--text-secondary)', opacity: 0.5 },
  },
};

const ChangeBalance: React.FC<ChangeBalanceProps> = ({ title, type }) => {
  const { id: userId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const [amount, setAmount] = useState('');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState<{ show: boolean; type: 'success' | 'error'; message: string }>(
    {
      show: false,
      type: 'success',
      message: '',
    },
  );
  const [showWarningModal, setShowWarningModal] = useState(false);
  const idempotencyKeyRef = useRef<string | null>(null);

  const { userDetail, isLoadingSmall } = useAppSelector((state) => state.users);

  const usdBalance = userDetail?.balances?.find((b) => b.currency === 'USD');
  const balanceAmount = usdBalance?.balance || 0;
  const withdrawableAmount = usdBalance?.withdrawable || 0;

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const sanitizedValue = sanitizeNumeric(value, true);
    setAmount(sanitizedValue);
    idempotencyKeyRef.current = null;
  };

  const handleCommentChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setComment(e.target.value);
    idempotencyKeyRef.current = null;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting || !amount || !comment || !userId) return;

    if (type === 'decrease' && parseFloat(amount) > parseFloat(String(withdrawableAmount))) {
      setShowWarningModal(true);
      return;
    }

    processTransaction();
  };

  const processTransaction = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setShowWarningModal(false);

    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = generateIdempotencyKey();
    }
    const idempotencyKey = idempotencyKeyRef.current;

    try {
      const payload = {
        currencyCode: 'USD',
        paymentCode: 'manual',
        amount,
        comment,
      };

      if (type === 'increase') {
        await userService.creditBalance(userId!, payload, idempotencyKey);
      } else {
        await userService.debitBalance(userId!, payload, idempotencyKey);
      }

      const successMsg = `Balance ${type}d successfully`;
      setAlert({ show: true, type: 'success', message: successMsg });
      dispatch(showToast({ text: successMsg, type: 'success' }));
      dispatch(fetchUserById(userId!));
      setAmount('');
      setComment('');
      idempotencyKeyRef.current = null;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || error.message || `Failed to ${type} balance`;
      setAlert({ show: true, type: 'error', message: errorMsg });
      dispatch(showToast({ text: errorMsg, type: 'error' }));
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setAlert((prev) => ({ ...prev, show: false })), 4000);
    }
  };

  return (
    <section className={styles.container}>
      <Typography variant="h6" className={styles.title}>
        {title}
      </Typography>

      {type === 'decrease' && usdBalance && (
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
              Balance (USD):
            </Typography>
            {isLoadingSmall ? (
              <Skeleton width={60} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
            ) : (
              <Typography variant="body2" sx={{ color: 'var(--accent-blue)', fontWeight: 600 }}>
                {balanceAmount}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ color: 'var(--text-primary)' }}>
              Withdrawable (USD):
            </Typography>
            {isLoadingSmall ? (
              <Skeleton width={60} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
            ) : (
              <Typography variant="body2" sx={{ color: 'var(--accent-blue)', fontWeight: 600 }}>
                {withdrawableAmount}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {alert.show && <Alert type={alert.type} message={alert.message} className={styles.alert} />}

      <form onSubmit={handleSubmit} className={styles.form}>
        <Box className={styles.row}>
          <TextField
            fullWidth
            size="small"
            label="Amount($)"
            value={amount}
            onChange={handleAmountChange}
            autoComplete="off"
            required
            disabled={isSubmitting}
            sx={inputStyles}
          />
        </Box>

        <TextField
          fullWidth
          size="small"
          label="Comment"
          multiline
          rows={2}
          value={comment}
          onChange={handleCommentChange}
          required
          disabled={isSubmitting}
          sx={inputStyles}
        />

        <Box className={styles.actions}>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            className={styles.submitBtn}
            disabled={!amount || !comment || isSubmitting}
          >
            {isSubmitting ? 'Processing...' : `${title.split(' ')[0]} Balance`}
          </Button>
        </Box>
      </form>

      <ConfirmModal
        open={showWarningModal}
        title="Warning"
        description={`The amount you entered ($${amount}) is greater than the user's withdrawable balance ($${withdrawableAmount}). Do you want to continue with this decrease?`}
        onClose={() => setShowWarningModal(false)}
        onConfirm={processTransaction}
        confirmText="Continue"
        cancelText="Cancel"
        isDanger={true}
      />
    </section>
  );
};

export default ChangeBalance;
